export interface CsvColumn<T> {
  header: string;
  value: (row: T) => string | number | null | undefined;
}

export interface CsvOptions {
  /**
   * Prefix data fields starting with a spreadsheet formula trigger (`=`, `+`,
   * `@`, or a non-numeric leading `-`) with a `'` so untrusted values cannot
   * execute as formulas when the CSV is opened in Excel.
   */
  guardFormulas?: boolean;
}

/**
 * Escapes a single CSV field per RFC 4180: values containing a comma, double
 * quote, or line break are wrapped in double quotes, and inner double quotes are
 * doubled. `null`/`undefined` become an empty cell.
 */
const escapeCsvField = (
  value: string | number | null | undefined,
  options?: CsvOptions
): string => {
  let str = value === null || value === undefined ? '' : String(value);
  if (options?.guardFormulas === true && /^[=+@-]/.test(str) && !/^-?\d+(\.\d+)?$/.test(str)) {
    str = `'${str}`;
  }
  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

/**
 * UTF-8 byte-order mark (U+FEFF). Prepend it to a CSV file so spreadsheet apps
 * (e.g. Excel) detect the encoding and render accented characters correctly.
 */
export const UTF8_BOM = String.fromCharCode(0xfeff);

/**
 * Serialises rows into an RFC 4180 CSV string (CRLF line breaks, no trailing
 * newline). Column order and headers are taken from `columns`; `options` apply
 * to data fields only, never headers.
 */
export const rowsToCsv = <T>(columns: CsvColumn<T>[], rows: T[], options?: CsvOptions): string => {
  const headerLine = columns.map(column => escapeCsvField(column.header)).join(',');
  const dataLines = rows.map(row =>
    columns.map(column => escapeCsvField(column.value(row), options)).join(',')
  );
  return [headerLine, ...dataLines].join('\r\n');
};
